<template>
  <esg-base-chart max-width="500px">
    <loading :active="loading"
             :z-index="1000"
             :is-full-page="false"/>
    <apex-chart type="bar"
                ref="chart"
                :width=this.width
                :height=this.height
                v-show="!loading"
                :options="chartOptions"
                :series="series"/>
  </esg-base-chart>
</template>

<script>
import EsgBaseChart from "@/components/esg/base/EsgBaseChart.vue";

export default {
  components: { EsgBaseChart },
  props: {
    width: {
      type: String,
      default: '100%'
    },
    height: {
      type: String,
      default: '250'
    },
    legend: {
      type: String,
      default: 'right'
    },
    sort: {
      type: String,
      default: 'dsc'
    }
  },
  created() {
    this.fetchData();
  },
  beforeDestroy() {
    if (this.abortController) {
      this.abortController.abort();
    }
  },
  watch: {
    sort: {
      immediate: true,
      handler() {
        this.sortSeries(this.sort);
      }
    }
  },
  data() {
    return {
      loading: false,
      corporations: [],
      ascCorporations: [],
      dscCorporations: [],
      ascLabels: [],
      dscLabels: [],
      ascSeries: [],
      dscSeries: [],
      series: [],
      chartOptions: {
        chart: {
          type: "bar",
          stacked: true,
          fontFamily: 'NanumGothic',
          toolbar: { show: false },
          zoom: { enabled: false },
          events: {
            dataPointSelection: (_1, _2, config) => {
              this.$emit('dataPointSelection', this.corporations[config.dataPointIndex]);
            },
            dataPointMouseEnter: (event) => {
              event.target.style.cursor = "pointer"
            }
          }
        },
        plotOptions: {
          bar: {
            dataLabels: {
              total: {
                enabled: true,
                formatter: val => this.formatNumber(val, 0),
                style: {
                  fontWeight: 'normal',
                  colors: ['#000']
                },
              }
            }
          },
        },
        xaxis: {
          categories: [],
          crosshairs: { show: false }
        },
        yaxis: {
          show: true,
          title: {
            text: '탄소 배출량 (tCO2)',
            style: {
              fontSize: '12px',
              fontWeight: 'normal',
            },
          },
          labels: {
            formatter: val => this.compactFormatNumber(val),
            minWidth: 30,
          },
          crosshairs: { show: false }
        },
        dataLabels: { enabled: false, },
        tooltip: {
          y: {
            formatter: val => this.formatNumber(val, 2) + ' tCO2'
          }
        },
        grid: {
          show: false,
        },
        legend: { position: this.legend },
        states: {
          active: {
            filter: {
              type: 'none'
            }
          }
        }
      },
    }
  },
  methods: {
    async fetchData() {
      try {
        this.loading = true;

        // Fetch corporations
        let response = await axios.get('esg/lca/tables/esg_base_corporation', {
          headers: { Authorization: accessToken() }
        });
        this.corporations = response.data.list;

        // Fetch chart data
        let year = new Date().getFullYear();
        let years = [year];
        let scopes = [1, 2, 3]
        let args = [{
          years,
          scopes,
          groupKeys: ['year', 'scope', 'corporation_code']
        }];

        let data = new FormData()
        data.append('path', 'esg/ghg_emission/main');
        data.append('json', JSON.stringify(args));

        this.abortController = new AbortController();
        response = await axios.post('esg/lca/run/python/chart-data', data, {
          headers: { Authorization: accessToken() },
          signal: this.abortController.signal
        });

        if (!response.data.data) {
          this.series = [];
          return;
        }

        let list = JSON.parse(response.data.data)[0].data;

        this.setTotal(list, this.corporations);
        this.setSeriesAndLabels(list, scopes, this.corporations);
        this.sortSeries(this.sort);
      } catch (error) {
        this.loading = false;
        await this.showError(error);
      } finally {
        this.loading = false;
        this.refreshChart();
      }
    },
    setTotal(list, corporations) {
      corporations.forEach(corp => {
        corp.total = list.filter(row => row.corporation_code === corp.code)
            .reduce((prev, row) => prev + row.values, 0);
      });
    },
    setSeriesAndLabels(list, scopes, corporations) {
      // Sort corporations in ascending order of the total
      corporations.sort((a, b) => a.total - b.total);

      // Set corporations for ascending and descending order
      this.ascCorporations = this.corporations;
      this.dscCorporations = structuredClone(this.corporations);
      this.dscCorporations.reverse();

      // Set labels for ascending and descending order
      this.ascLabels = corporations.map(corp => corp.name);
      this.dscLabels = structuredClone(this.ascLabels);
      this.dscLabels.reverse();

      // Set series
      let series = [];
      scopes.forEach(scope => {
        let data = [];
        corporations.forEach(corporation => {
          let row = list.find(row => row.corporation_code === corporation.code && row.scope === scope);
          data.push(row ? row.values : 0);
        });
        series.push({
          name: 'Scope ' + scope,
          type: 'column',
          data
        });
      });
      this.ascSeries = series;
      this.dscSeries = structuredClone(this.ascSeries);
      this.dscSeries.forEach(series => series.data.reverse());
    },
    sortSeries(sort) {
      if (sort === 'dsc') {
        this.series = this.dscSeries
        this.chartOptions.xaxis.categories = this.dscLabels;
        this.corporations = this.dscCorporations;
      } else {
        this.series = this.ascSeries;
        this.chartOptions.xaxis.categories = this.ascLabels;
        this.corporations = this.ascCorporations;
      }
      this.refreshChart();
    }
  },
}
</script>